import type { GeneralInfoPicklistItem } from '../api/generalInfoApi';
import type { GeoCoordinates } from './assetGeo';
import {
  ASSET_SEARCH_CATALOG_TTL_MS,
  ASSET_SEARCH_PAGE_SIZE,
  fetchActiveAssetCatalogStaged,
  fetchSearchAssetsPage,
  invalidateActiveAssetCatalogCache,
  seedActiveAssetCatalogCache,
} from '../api/assetSearchClient';
import {
  pickAssetsInVicinity,
  pickRecommendedInAreaAssets,
  assetReviewCount,
  sortAssetsAlphabetically,
  sortAssetsByBookingCount,
  sortAssetsByReviewCount,
  takeTopAssets,
} from './homeAssetRanking';
import {
  excludeHomeGymCarouselAssets,
  filterHomeGymCarouselAssets,
} from './homeCarouselAssetMatch';

/** First paint: one page (or one fetch_all) is enough for home carousels. */
const HOME_POOL_FIRST_PAGES = 1;

const HOME_POOL_STORAGE_KEY = 'ofrontend.homeAssetPool.v1';

export type HomeCarouselBundle = {
  recommended: Record<string, unknown>[];
  vicinity: Record<string, unknown>[];
  gyms: Record<string, unknown>[];
  popular: Record<string, unknown>[];
  frequentlyBooked: Record<string, unknown>[];
  catalog: Record<string, unknown>[];
};

type CachedPool = {
  records: Record<string, unknown>[];
  fetchedAt: number;
};

let memoryPool: CachedPool | null = null;
let homeBrowseCatalogPromise: Promise<Record<string, unknown>[]> | null = null;

function isPoolFresh(fetchedAt: number): boolean {
  return Date.now() - fetchedAt < ASSET_SEARCH_CATALOG_TTL_MS;
}

function readSessionPool(): CachedPool | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(HOME_POOL_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as CachedPool;
    if (
      !parsed ||
      typeof parsed.fetchedAt !== 'number' ||
      !Array.isArray(parsed.records) ||
      !isPoolFresh(parsed.fetchedAt)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeSessionPool(cache: CachedPool): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(HOME_POOL_STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // quota / private mode — memory cache still works
  }
}

async function fetchPlainActivePage(size = ASSET_SEARCH_PAGE_SIZE): Promise<Record<string, unknown>[]> {
  const page = await fetchSearchAssetsPage({}, 0, size);
  return page.records;
}

/**
 * One shared active-asset dump for the whole home page.
 * Prefers a single `fetch_all` round-trip, then one list page, then staged catalog.
 */
export async function fetchHomeRankingPool(): Promise<Record<string, unknown>[]> {
  if (memoryPool && isPoolFresh(memoryPool.fetchedAt) && memoryPool.records.length > 0) {
    return memoryPool.records;
  }

  const session = readSessionPool();
  if (session && session.records.length > 0) {
    memoryPool = session;
    return session.records;
  }

  if (!homeBrowseCatalogPromise) {
    homeBrowseCatalogPromise = (async () => {
      let records: Record<string, unknown>[] = [];

      try {
        const dump = await fetchSearchAssetsPage({ fetchAll: true }, 0, 0);
        if (dump.records.length > 0) {
          records = dump.records;
          seedActiveAssetCatalogCache(records, dump.total);
        }
      } catch {
        // fetch_all may be unsupported for non-keyword — fall through
      }

      if (records.length === 0) {
        try {
          const staged = await fetchActiveAssetCatalogStaged({
            firstPages: HOME_POOL_FIRST_PAGES,
            maxPages: HOME_POOL_FIRST_PAGES,
          });
          records = staged.records;
        } catch {
          records = await fetchPlainActivePage().catch(() => []);
        }
      }

      if (records.length === 0) {
        homeBrowseCatalogPromise = null;
        return [];
      }

      const cache: CachedPool = { records, fetchedAt: Date.now() };
      memoryPool = cache;
      writeSessionPool(cache);
      return records;
    })().catch((err) => {
      homeBrowseCatalogPromise = null;
      throw err;
    });
  }

  return homeBrowseCatalogPromise;
}

/** Drop memory + session home pool (logout). */
export function clearHomeRankingPoolCache(): void {
  homeBrowseCatalogPromise = null;
  memoryPool = null;
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(HOME_POOL_STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}

/** Build every home carousel from one pool (no per-carousel network). */
export function buildHomeCarouselsFromPool(
  pool: Record<string, unknown>[],
  coordinates: GeoCoordinates | null | undefined,
  typeCatalog: GeneralInfoPicklistItem[],
  limit: number,
): HomeCarouselBundle {
  const withoutGyms = excludeHomeGymCarouselAssets(pool, typeCatalog);
  const gymsPool = filterHomeGymCarouselAssets(pool, typeCatalog);

  const recommendedPick = pickRecommendedInAreaAssets(withoutGyms, coordinates, limit);
  const recommended =
    recommendedPick.length > 0
      ? recommendedPick
      : takeTopAssets(sortAssetsAlphabetically(withoutGyms), limit);

  const vicinityPick = pickAssetsInVicinity(withoutGyms, coordinates, limit);
  const vicinity =
    vicinityPick.length > 0
      ? vicinityPick
      : takeTopAssets(sortAssetsAlphabetically(withoutGyms), limit);

  const gymPicked = pickAssetsInVicinity(gymsPool, coordinates, limit);
  const gyms =
    gymPicked.length > 0
      ? gymPicked
      : takeTopAssets(sortAssetsAlphabetically(gymsPool), limit);

  const popular = withoutGyms.some((asset) => assetReviewCount(asset) > 0)
    ? takeTopAssets(sortAssetsByReviewCount(withoutGyms), limit)
    : takeTopAssets(sortAssetsAlphabetically(withoutGyms), limit);

  const bookedSorted = sortAssetsByBookingCount(pool);
  const frequentlyBooked =
    bookedSorted.length > 0
      ? takeTopAssets(bookedSorted, limit)
      : takeTopAssets(sortAssetsAlphabetically(pool), limit);

  return {
    recommended,
    vicinity,
    gyms,
    popular,
    frequentlyBooked,
    catalog: pool,
  };
}

/** Single entry: one network pool (cached) → all carousels. */
export async function loadHomeCarousels(input: {
  coordinates?: GeoCoordinates | null;
  typeCatalog: GeneralInfoPicklistItem[];
  limit: number;
}): Promise<HomeCarouselBundle> {
  const pool = await fetchHomeRankingPool();
  if (pool.length === 0) {
    return {
      recommended: [],
      vicinity: [],
      gyms: [],
      popular: [],
      frequentlyBooked: [],
      catalog: [],
    };
  }
  return buildHomeCarouselsFromPool(
    pool,
    input.coordinates,
    input.typeCatalog,
    input.limit,
  );
}

/** @deprecated Prefer {@link loadHomeCarousels}. */
export async function fetchHomeRecommendedAssets(
  coordinates: GeoCoordinates | null | undefined,
  limit: number,
  typeCatalog: GeneralInfoPicklistItem[] = [],
): Promise<Record<string, unknown>[]> {
  return (await loadHomeCarousels({ coordinates, typeCatalog, limit })).recommended;
}

/** @deprecated Prefer {@link loadHomeCarousels}. */
export async function fetchHomeVicinityAssets(
  coordinates: GeoCoordinates | null | undefined,
  limit: number,
  typeCatalog: GeneralInfoPicklistItem[] = [],
): Promise<Record<string, unknown>[]> {
  return (await loadHomeCarousels({ coordinates, typeCatalog, limit })).vicinity;
}

/** @deprecated Prefer {@link loadHomeCarousels}. */
export async function fetchHomeGymVicinityAssets(
  coordinates: GeoCoordinates | null | undefined,
  typeCatalog: GeneralInfoPicklistItem[],
  limit: number,
): Promise<Record<string, unknown>[]> {
  return (await loadHomeCarousels({ coordinates, typeCatalog, limit })).gyms;
}

/** @deprecated Prefer {@link loadHomeCarousels}. */
export async function fetchHomePopularAssets(
  limit: number,
  typeCatalog: GeneralInfoPicklistItem[] = [],
): Promise<Record<string, unknown>[]> {
  return (await loadHomeCarousels({ coordinates: null, typeCatalog, limit })).popular;
}

/** @deprecated Prefer {@link loadHomeCarousels}. */
export async function fetchHomeFrequentlyBookedAssets(
  limit: number,
): Promise<Record<string, unknown>[]> {
  return (await loadHomeCarousels({ coordinates: null, typeCatalog: [], limit })).frequentlyBooked;
}

/** @deprecated Prefer {@link loadHomeCarousels}. */
export async function fetchHomeBrowseCatalog(): Promise<Record<string, unknown>[]> {
  return fetchHomeRankingPool();
}

/** Reset ranking + active catalog caches (logout). */
export function resetHomeAssetSearchCaches(): void {
  clearHomeRankingPoolCache();
  invalidateActiveAssetCatalogCache();
}
